import React, { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, CircularProgress, Dialog, DialogContent, IconButton, Stack, Typography, useTheme } from '@mui/material';

import { PlatformLocale, useGetOrganizationMessages, useGetProcessMessages } from '@/api/generated';
import XBigIcon from '@/assets/icons/x-big.svg?react';
import DropdownSelect from '@/components/fields/DropdownSelect';
import RichTextLabel from '@/components/RichTextLabel/RichTextLabel';
import typography from '@/theme/typography';

interface MessageViewModalProps {
  open: boolean;
  onClose: () => void;
  msgType: string;
  icon?: React.ReactNode;
  organizationSlug?: string;
  processId?: string;
}

const MessageViewModal: FC<MessageViewModalProps> = ({ open, onClose, msgType, icon, organizationSlug, processId }) => {
  const { t, i18n } = useTranslation('common');
  const theme = useTheme();
  const { spacing } = theme;
  const [selectedLanguage, setSelectedLanguage] = useState(i18n.language as PlatformLocale);

  const { data: { messages: processMessages } = {}, isLoading: isProcessMessagesLoading } = useGetProcessMessages(
    processId!,
    { locale: selectedLanguage as PlatformLocale },
    {
      query: {
        enabled: !!processId
      }
    }
  );

  const { data: { messages: organizationMessages } = {}, isLoading: isOrganizationMessagesLoading } =
    useGetOrganizationMessages(
      organizationSlug!,
      { locale: selectedLanguage },
      {
        query: {
          enabled: !!organizationSlug
        }
      }
    );

  const messagesList = Object.entries(processMessages || organizationMessages || {}).map(([msgType, msgContent]) => ({
    langId: selectedLanguage,
    msgType,
    msgContent,
    msgTitle: t(`processes:layouts.process.messages.${msgType}`, { defaultValue: msgType })
  }));

  const isLoading = isProcessMessagesLoading || isOrganizationMessagesLoading;

  const currentMessage = useMemo(
    () => messagesList?.find(message => message.msgType === msgType),
    [messagesList, msgType]
  );

  const languageOptions = useMemo(
    () =>
      Object.keys(PlatformLocale).map(locale => ({
        value: locale,
        label: t(`common.languages.${locale}`)
      })),
    [t]
  );

  const handleLanguageChange = useCallback((newValue: string | string[]) => {
    setSelectedLanguage(newValue as PlatformLocale);
  }, []);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: spacing(1),
          maxHeight: '90vh',
          bgcolor: 'background.default'
        }
      }}
    >
      <Box
        sx={{
          position: 'relative',
          p: spacing(3)
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" gap={spacing(2)}>
            {icon && (
              <Box
                sx={{
                  width: spacing(3),
                  height: spacing(3),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}
              >
                {icon}
              </Box>
            )}
            <Typography
              variant="h6"
              sx={{
                fontWeight: typography.fontWeightMedium,
                color: 'text.primary'
              }}
            >
              {currentMessage?.msgTitle}
            </Typography>
          </Stack>

          <Stack direction="row" alignItems="center" gap={spacing(2)}>
            <Box sx={{ minWidth: 200 }}>
              <Typography variant="body3" color="grey.700">
                {t('common.languages.list')}
              </Typography>
              <DropdownSelect
                name="language"
                value={selectedLanguage}
                onChange={handleLanguageChange}
                options={languageOptions}
                isMulti={false}
                isSearchable={true}
                placeholder={t('common.languages.list')}
                sx={{ minWidth: 200 }}
                usePortal
              />
            </Box>

            <IconButton onClick={onClose} size="small">
              <XBigIcon />
            </IconButton>
          </Stack>
        </Stack>
      </Box>

      <DialogContent
        sx={{
          p: spacing(3),
          bgcolor: 'background.default',
          overflow: 'auto'
        }}
      >
        {isLoading && <CircularProgress size={24} />}
        {!isLoading && !currentMessage && (
          <Box sx={{ p: 3, textAlign: 'center' }}>{t('common:error.somethingWentWrong')}</Box>
        )}
        {!isLoading && !!currentMessage && (
          <Stack gap={spacing(2)}>
            <Typography
              variant="body1"
              sx={{
                fontWeight: typography.fontWeightMedium,
                color: 'text.primary',
                mb: spacing(1)
              }}
            >
              {currentMessage?.msgTitle}
            </Typography>

            <Box
              sx={{
                color: 'text.primary',
                lineHeight: 1.6,
                '& p': {
                  marginBottom: spacing(1)
                }
              }}
            >
              <RichTextLabel html={currentMessage?.msgContent} />
            </Box>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MessageViewModal;
